import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;
const HEARTBEAT_MS = 30 * 1000;
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];

const MODULE_MAP = [
  [/^\/onboarding/, 'Client Onboarding'],
  [/^\/onboard/, 'Client Onboarding'],
  [/^\/client-portal/, 'Client Portal'],
  [/^\/client-management/, 'Client Portal'],
  [/^\/esign/, 'E-Sign'],
  [/^\/id-verification/, 'ID Verification'],
  [/^\/verify/, 'ID Verification'],
  [/^\/lead-nurture|^\/lead-connect|^\/leads/, 'Lead Nurture'],
  [/^\/game-plan/, 'Game Plan Tracker'],
  [/^\/myweek/, 'MyWeek'],
  [/^\/drop-drive|^\/dropdrive/, 'Drop Drive'],
  [/^\/lender-packs/, 'Lender Packs'],
  [/^\/post-settlement/, 'Post-Settlement'],
  [/^\/compliance/, 'Compliance'],
  [/^\/bdm-contacts/, 'BDM Contacts'],
  [/^\/hot-off-the-press|^\/news/, 'Hot off the Press'],
  [/^\/work|^\/todos/, 'Work'],
  [/calculator/, 'Calculators'],
  [/^\/$|^\/home/, 'Home'],
];
const moduleFromPath = (p) => { for (const [re, n] of MODULE_MAP) if (re.test(p)) return n; return 'DerwentAI'; };

const WorkforceContext = createContext(null);
export const useWorkforce = () => useContext(WorkforceContext) || { loading: true, enabled: false };

// Global workforce tracker — mounted once near the app root so attendance and
// activity are captured on EVERY page, while the visible clock in/out control
// lives in the top bar. Privacy-safe: only module + active/idle, never keystrokes.
export const WorkforceProvider = ({ children }) => {
  const { token, user } = useAuth();
  const location = useLocation();
  const [state, setState] = useState({ loading: true, enabled: false, session: null, idleThresholdMin: 5 });
  const [busy, setBusy] = useState(false);

  const lastActivityRef = useRef(Date.now());
  const sessionRef = useRef(null);
  const thresholdRef = useRef(5);
  const moduleRef = useRef(moduleFromPath(location.pathname));

  const headers = useCallback(() => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }), [token]);
  useEffect(() => { moduleRef.current = moduleFromPath(location.pathname); }, [location.pathname]);

  const applySession = useCallback((session) => {
    sessionRef.current = session;
    setState((s) => ({ ...s, session }));
  }, []);

  const refresh = useCallback(async () => {
    if (!token) { setState((s) => ({ ...s, loading: false, enabled: false })); return; }
    try {
      const res = await fetch(`${API}/api/workforce/me/today`, { headers: headers() });
      if (!res.ok) throw new Error('today');
      const data = await res.json();
      sessionRef.current = data.session || null;
      thresholdRef.current = data.idle_threshold_min || 5;
      setState({ loading: false, enabled: !!data.tracking_enabled, session: data.session || null, idleThresholdMin: data.idle_threshold_min || 5 });
    } catch {
      setState((s) => ({ ...s, loading: false, enabled: false }));
    }
  }, [token, headers]);

  useEffect(() => { refresh(); }, [refresh]);

  const act = useCallback(async (path) => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/workforce/${path}`, { method: 'POST', headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Action failed');
      if (data.session) applySession(data.session);
      return data;
    } finally { setBusy(false); }
  }, [headers, applySession]);

  const checkIn = useCallback(() => act('check-in'), [act]);
  const breakStart = useCallback(() => act('break/start'), [act]);
  const breakEnd = useCallback(() => act('break/end'), [act]);
  const finish = useCallback(() => act('finish'), [act]);

  useEffect(() => {
    if (!state.enabled) return undefined;
    const mark = () => { lastActivityRef.current = Date.now(); };
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, mark, { passive: true }));
    return () => ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, mark));
  }, [state.enabled]);

  useEffect(() => {
    if (!state.enabled || !token) return undefined;
    const tick = async () => {
      const sess = sessionRef.current;
      if (!sess || sess.status !== 'working') return;
      const idleMs = thresholdRef.current * 60 * 1000;
      const active = document.visibilityState === 'visible' && (Date.now() - lastActivityRef.current) < idleMs;
      try {
        await fetch(`${API}/api/workforce/heartbeat`, { method: 'POST', headers: headers(), body: JSON.stringify({ active, module: moduleRef.current }) });
      } catch { /* non-fatal */ }
    };
    const id = setInterval(tick, HEARTBEAT_MS);
    tick();
    return () => clearInterval(id);
  }, [state.enabled, state.session?.status, token, headers]);

  const value = { ...state, busy, user, checkIn, breakStart, breakEnd, finish, refresh };
  return <WorkforceContext.Provider value={value}>{children}</WorkforceContext.Provider>;
};
